/**
 * WebSocket envelope + message types.
 *
 * Mirrors ai-pacs-copilot `copilot/protocol.py` — keep in sync. Voice
 * dictation rides the same JSON channel: the client streams base64 LINEAR16
 * PCM as `voice_chunk` frames and signals `end_voice`; the server replies with
 * interim/final `transcript` frames.
 */

export const PROTOCOL_VERSION = 1;
export const WS_SUBPROTOCOL = 'copilot.v1';

export type ClientMessageType =
  | 'user_message'
  | 'ping'
  | 'resume'
  | 'ack'
  | 'voice_chunk'
  | 'end_voice';

export type ServerMessageType =
  | 'token'
  | 'thinking'
  | 'tool_call_start'
  | 'tool_call_delta'
  | 'tool_call_end'
  | 'effect'
  | 'done'
  | 'error'
  | 'pong'
  | 'transcript';

export interface Envelope<T = unknown> {
  v: number;
  id: string;
  ts: string;
  type: ClientMessageType | ServerMessageType;
  payload: T;
  reply_to?: string;
  seq?: number;
}

// --- Server → client payloads ---------------------------------------------
export interface TokenPayload {
  text: string;
}
export interface ThinkingPayload {
  thinking: string;
}
export interface ToolCallStartPayload {
  tool_id: string;
  name: string;
  input?: unknown;
}
export interface ToolCallEndPayload {
  result?: unknown;
  effects?: EffectFrame[];
}
export interface EffectFrame {
  type: string;
  payload: Record<string, unknown>;
}
export interface DonePayload {
  ready?: boolean;
  conversation_uuid?: string;
  stop_reason?: string;
}
export interface ErrorPayload {
  message: string;
  detail?: string;
  retry_after_s?: number;
}
export interface TranscriptPayload {
  text: string;
  final: boolean;
}

// --- Client → server payloads ---------------------------------------------
export interface UserMessagePayload {
  text: string;
  study_context?: Record<string, unknown>;
}
export interface ResumePayload {
  last_ack_seq: number;
}
export interface VoiceChunkPayload {
  /** base64-encoded LINEAR16 (Int16 LE) mono PCM at 16 kHz. */
  audio_b64: string;
}

let messageCounter = 0;

export function makeMessageId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  messageCounter += 1;
  return `m-${Date.now()}-${messageCounter}`;
}

export function buildEnvelope<T>(
  type: ClientMessageType,
  payload: T
): Envelope<T> {
  return {
    v: PROTOCOL_VERSION,
    id: makeMessageId(),
    ts: new Date().toISOString(),
    type,
    payload,
  };
}
